export const OPC_DATA_TYPES = [
    "Boolean",
    "SByte",
    "Byte",
    "Int16",
    "UInt16",
    "Int32",
    "UInt32",
    "Int64",
    "UInt64",
    "Float",
    "Double",
    "String",
];

export const RECORDING_MODES = [
    { value: "ALWAYS", label: "Always (Monitoring)" },
    { value: "ON_CHANGE", label: "On Change" },
];

export const ON_CHANGE_SECOND_CHOICES = [5, 10, 20, 30, 40, 50];
export const ON_CHANGE_MINUTE_CHOICES = Array.from({ length: 60 }, (_, index) => (index + 1) * 60);
export const ON_CHANGE_INTERVAL_SECONDS = [...ON_CHANGE_SECOND_CHOICES, ...ON_CHANGE_MINUTE_CHOICES];
export const ON_CHANGE_INTERVAL_PRESETS = [5, 10, 20, 30, 40, 50, 60, 120, 300, 600, 900, 1200, 1800, 2700, 3600];
export const ON_CHANGE_INTERVAL_MINUTES = Array.from({ length: 60 }, (_, index) => index + 1);

export function intervalLabel(seconds) {
    const count = Number(seconds);
    if (!Number.isFinite(count)) {
        return "";
    }
    if (count < 60) {
        return `${count} ${count === 1 ? "second" : "seconds"}`;
    }
    const minutes = count / 60;
    if (minutes === 60) {
        return "1 hour";
    }
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

export function intervalMinuteLabel(minutes) {
    return intervalLabel(Number(minutes) * 60);
}

export function intervalSecondsFromForm(value) {
    const seconds = Number(value);
    return ON_CHANGE_INTERVAL_SECONDS.includes(seconds) ? seconds : 300;
}

const INTEGER_TYPES = new Set(["SByte", "Byte", "Int16", "UInt16", "Int32", "UInt32", "Int64", "UInt64"]);
const FLOAT_TYPES = new Set(["Float", "Double"]);

export function manualDefaults() {
    return {
        server: "",
        nodeId: "",
        name: "",
        signalType: "ANALOG",
        opcDataType: "Float",
        accessMode: "READ_ONLY",
        group: "",
        lifecycle: "active",
        description: "",
        isCalculated: false,
        formula: "",
        recordingMode: "ALWAYS",
        decimalPlaces: "2",
        onChangeIntervalSeconds: "300",
    };
}

export function decimalDefault(signalType, opcDataType) {
    if (signalType === "STRING" || opcDataType === "String") {
        return "";
    }
    if (signalType === "DIGITAL" || opcDataType === "Boolean") {
        return "0";
    }
    if (INTEGER_TYPES.has(opcDataType)) {
        return "0";
    }
    return "2";
}

export function dataTypeForSignal(signalType, currentType) {
    if (signalType === "STRING") {
        return "String";
    }
    if (signalType === "DIGITAL") {
        if (currentType === "Boolean" || INTEGER_TYPES.has(currentType)) {
            return currentType;
        }
        return "Boolean";
    }
    if (FLOAT_TYPES.has(currentType) || INTEGER_TYPES.has(currentType)) {
        return currentType;
    }
    return "Float";
}

export function signalAcceptsDataType(signalType, opcDataType) {
    if (!OPC_DATA_TYPES.includes(opcDataType)) {
        return false;
    }
    if (signalType === "STRING") {
        return opcDataType === "String";
    }
    if (opcDataType === "String") {
        return false;
    }
    if (signalType === "DIGITAL") {
        return opcDataType === "Boolean" || INTEGER_TYPES.has(opcDataType);
    }
    if (signalType === "ANALOG") {
        return FLOAT_TYPES.has(opcDataType) || INTEGER_TYPES.has(opcDataType);
    }
    return false;
}

export function groupDeniesWrite(groups, groupId) {
    if (!groupId) {
        return false;
    }
    const byId = new Map((groups || []).map((group) => [String(group.id), group]));
    const seen = new Set();
    let current = byId.get(String(groupId));
    while (current && !seen.has(String(current.id))) {
        seen.add(String(current.id));
        if (current.default_access_mode === "READ_ONLY") {
            return true;
        }
        current = current.parent ? byId.get(String(current.parent)) : null;
    }
    return false;
}

export function groupChoices(groups) {
    const rows = Array.isArray(groups) ? groups : [];
    const children = new Map();
    rows.forEach((group) => {
        const parentKey = group.parent == null || group.parent === "" ? "" : String(group.parent);
        const list = children.get(parentKey) || [];
        list.push(group);
        children.set(parentKey, list);
    });
    const choices = [{ value: "", label: "No group", depth: 0 }];
    const walk = (parentKey, depth) => {
        const list = (children.get(parentKey) || []).slice().sort((left, right) => (
            String(left.name || "").localeCompare(String(right.name || ""))
        ));
        list.forEach((group) => {
            choices.push({
                value: String(group.id),
                label: group.name || `Group ${group.id}`,
                depth,
            });
            walk(String(group.id), depth + 1);
        });
    };
    walk("", 1);
    return choices;
}

function availableText(field) {
    if (!field || field.available !== true) {
        return "";
    }
    return field.value == null ? "" : String(field.value);
}

export function browserDraft(details, listing, serverId) {
    const dataType = availableText(details?.data_type?.data_type_name);
    const rankKnown = details?.data_type?.value_rank?.available === true;
    const rank = rankKnown ? details.data_type.value_rank.value : null;
    const nodeClass = availableText(details?.identity?.node_class);
    const supported = OPC_DATA_TYPES.includes(dataType) && nodeClass === "Variable" && rank === -1;
    const suggested = details?.suggestion?.signal_type || "";
    const description = details?.identity?.description?.available === true
        ? (details.identity.description.value || "")
        : "";
    let reason = "";
    if (!details) {
        reason = "OPC metadata has not been read for this node.";
    } else if (nodeClass !== "Variable") {
        reason = "Only OPC UA Variable nodes can be registered as tags.";
    } else if (!rankKnown || rank !== -1) {
        reason = "Array and structure values are not stored as scalar tags.";
    } else if (!OPC_DATA_TYPES.includes(dataType)) {
        reason = "Array and structure nodes are not stored as scalar tags.";
    }
    let signalType = "";
    if (dataType === "String") {
        signalType = "STRING";
    } else if (supported) {
        signalType = suggested;
    }
    return {
        unsupported: !supported,
        reason,
        sessionCanWrite: details?.access?.session_current_write?.value === true,
        nodeDataType: dataType,
        values: {
            server: serverId == null ? "" : String(serverId),
            nodeId: listing?.node_id || availableText(details?.identity?.node_id),
            name: listing?.display_name || availableText(details?.identity?.display_name),
            signalType,
            opcDataType: supported ? dataType : "",
            accessMode: "READ_ONLY",
            group: "",
            lifecycle: "active",
            description,
            isCalculated: false,
            formula: "",
            recordingMode: "ALWAYS",
            decimalPlaces: supported ? decimalDefault(signalType, dataType) : "",
            onChangeIntervalSeconds: "300",
        },
    };
}

export function validateTagDraft(values, options = {}) {
    const errors = {};
    if (!String(values.server || "").trim()) {
        errors.server = "OPC server is required.";
    }
    if (!String(values.nodeId || "").trim()) {
        errors.nodeId = "Node ID is required.";
    }
    if (!String(values.name || "").trim()) {
        errors.name = "Tag name is required.";
    }
    if (values.signalType !== "ANALOG" && values.signalType !== "DIGITAL" && values.signalType !== "STRING") {
        errors.signalType = "Signal type is required.";
    }
    if (values.recordingMode !== "ALWAYS" && values.recordingMode !== "ON_CHANGE") {
        errors.recordingMode = "Recording mode is required.";
    }
    if (values.recordingMode === "ON_CHANGE") {
        const seconds = Number(values.onChangeIntervalSeconds);
        if (!ON_CHANGE_INTERVAL_SECONDS.includes(seconds)) {
            errors.onChangeIntervalSeconds = "Choose 5, 10, 20, 30, 40 or 50 seconds, or 1 to 60 minutes.";
        }
    }
    if (values.signalType === "STRING") {
        if (values.decimalPlaces !== "" && values.decimalPlaces != null) {
            errors.decimalPlaces = "Decimal places do not apply to string tags.";
        }
    } else if (values.signalType === "DIGITAL") {
        if (String(values.decimalPlaces) !== "0") {
            errors.decimalPlaces = "Digital tags use 0 decimal places.";
        }
    } else if (values.signalType === "ANALOG") {
        const places = Number(values.decimalPlaces);
        if (String(values.decimalPlaces).trim() === "" || !Number.isInteger(places) || places < 0 || places > 12) {
            errors.decimalPlaces = "Decimal places must be a whole number from 0 to 12.";
        }
    }
    if (!values.opcDataType) {
        errors.opcDataType = "OPC data type is required.";
    } else if (values.signalType && !signalAcceptsDataType(values.signalType, values.opcDataType)) {
        errors.opcDataType = "String tags use String. Digital tags use Boolean or an integer type. Analog tags use a numeric type.";
    }
    if (values.accessMode !== "READ_ONLY" && values.accessMode !== "READ_WRITE") {
        errors.accessMode = "Access mode is required.";
    }
    if (values.lifecycle !== "active" && values.lifecycle !== "archived") {
        errors.lifecycle = "Choose Active or Archived.";
    }
    if (options.mode === "browser" && options.nodeDataType && values.opcDataType && values.opcDataType !== options.nodeDataType) {
        errors.opcDataType = `The OPC node reports ${options.nodeDataType}. Choosing another type does not change the PLC node.`;
    }
    if (options.mode === "browser" && options.unsupported) {
        errors.opcDataType = options.reason || "This OPC node cannot be stored as a scalar measurement.";
    }
    if (values.accessMode === "READ_WRITE" && options.mode === "browser" && !options.sessionCanWrite) {
        errors.accessMode = "Read and write is available only when this OPC session has CurrentWrite.";
    }
    if (values.accessMode === "READ_WRITE" && groupDeniesWrite(options.groups, values.group)) {
        errors.accessMode = "A parent group is read only, so this tag cannot be read/write.";
    }
    if (values.signalType === "STRING" && values.isCalculated) {
        errors.signalType = "String tags cannot use a numeric formula.";
    }
    if (values.isCalculated) {
        if (values.signalType !== "ANALOG") {
            errors.signalType = "Calculated tags must be analog. The formula uses x as the raw numeric value.";
        }
        if (!String(values.formula || "").trim()) {
            errors.formula = "Calculated tags require a formula.";
        }
        if (values.accessMode === "READ_WRITE") {
            errors.accessMode = "Calculated tags are read only.";
        }
    } else if (String(values.formula || "").trim()) {
        errors.formula = "A physical tag stores the OPC value. Turn Calculated Tag on to use a formula.";
    }
    if (options.nodeShare && options.nodeShare.level === "blocked" && !values.isCalculated) {
        errors.nodeId = options.nodeShare.message;
    }
    if (String(values.description || "").trim().length > 1000) {
        errors.description = "Description must be 1000 characters or fewer.";
    }
    return errors;
}

export function nodeShareState(occupants, isCalculated) {
    const rows = Array.isArray(occupants) ? occupants : [];
    const physical = rows.filter((row) => !row.is_calculated);
    if (rows.length === 0) {
        return { level: "clear", message: "", physical, calculated: [] };
    }
    const calculated = rows.filter((row) => row.is_calculated);
    if (!isCalculated && physical.length > 0) {
        return {
            level: "blocked",
            message: "A physical tag already uses this OPC node. A second physical tag is blocked. Turn Calculated Tag on to share the node with its own formula.",
            physical,
            calculated,
        };
    }
    return {
        level: "warning",
        message: "This OPC node is already registered. A calculated tag can share it and stores its own history.",
        physical,
        calculated,
    };
}

export function editorPatchPayload(form) {
    const signalType = form.signal_type || null;
    const stringTag = signalType === "STRING";
    return {
        name: form.name,
        access_mode: form.access_mode || null,
        signal_type: signalType,
        recording_mode: form.recording_mode || null,
        on_change_interval_seconds: intervalSecondsFromForm(form.on_change_interval_seconds),
        on_change_interval_minutes: intervalSecondsFromForm(form.on_change_interval_seconds) % 60 === 0
            ? intervalSecondsFromForm(form.on_change_interval_seconds) / 60
            : null,
        decimal_places: stringTag
            ? null
            : signalType === "DIGITAL"
                ? 0
                : (form.decimal_places === "" || form.decimal_places == null ? null : Number(form.decimal_places)),
        group: form.group || null,
        description: form.description,
        opc_data_type: form.opc_data_type || null,
        is_calculated: stringTag ? false : Boolean(form.is_calculated),
        formula: stringTag ? "" : form.formula,
    };
}

export function creationPayload(values, opcNode) {
    const calculated = Boolean(values.isCalculated);
    const payload = {
        name: String(values.name || "").trim(),
        node_id: String(values.nodeId || "").trim(),
        server: values.server,
        signal_type: values.signalType,
        opc_data_type: values.opcDataType,
        access_mode: calculated ? "READ_ONLY" : values.accessMode,
        group: values.group || null,
        lifecycle: values.lifecycle,
        description: values.description || "",
        is_calculated: calculated,
        formula: calculated ? String(values.formula || "").trim() : "",
        recording_mode: values.recordingMode || "ALWAYS",
        on_change_interval_seconds: intervalSecondsFromForm(values.onChangeIntervalSeconds),
        on_change_interval_minutes: intervalSecondsFromForm(values.onChangeIntervalSeconds) % 60 === 0
            ? intervalSecondsFromForm(values.onChangeIntervalSeconds) / 60
            : null,
        decimal_places: values.signalType === "STRING" ? null : Number(values.decimalPlaces),
    };
    if (opcNode) {
        payload.opc_node = opcNode;
    }
    return payload;
}
